/**
 * 本人成績(成績表)。2026-09-18 発注者共有の現行シート「2025年【オペレーター】数字確認シート(スタッフ用)」の「成績表」タブを再現する(D-91)。
 *  ① その月の結果(提出・修正シフト時間 → 目標、実稼働・取得・1時間あたり・目標との差)
 *  ② 取得件数達成率 ／ 1時間取得件数の推移(本人と全体平均)
 *  ③ 月別の結果(年度 4月〜3月)
 *  ④ 日割り結果(その月の1日〜末日)
 *  ⑤ 全体平均との比較(月別)
 * 見た目は PL・日次【集計】と同じ(UIデザインシステム)。数字の計算は staffReport.ts(シートの式どおり)。
 *  - admin: 画面上部で名前を検索してアポインターを選ぶ(?op=。2026-10-15 発注者指示で選択から名前検索に)。月は ?month=
 *  - 日別・週別(月曜始まり・月内通番)・月別。週別・月別に毎日の推移グラフと会社別(時間・件数)のグラフ。2026-10-15 発注者指示
 *  - operator: 自分だけ。※ 全員の日次集計を読めないため、スタッフ本人が見るには「本人の数字と全体平均」を別に保存する仕組みが要る
 *    (スタッフのログイン方式が決まってから作る。P-12)
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import HelpTip from '@core/components/HelpTip';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import { useToast } from '@core/components/Toast';
import FilterBar from '@core/components/ui/FilterBar';
import MetricCard, { MetricRow } from '@core/components/ui/MetricCard';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import ProgressBar from '@core/components/ui/ProgressBar';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import StatusBadge from '@core/components/ui/StatusBadge';
import ToneValue, { type ValueTone } from '@core/components/ui/ToneValue';
import { addDays, derived, staffMetrics, sumAgg, type StaffMetrics } from '@core/dashboard/calc';
import { fetchMonthTargets, fetchSummariesRange } from '@core/dashboard/dashboardRepo';
import { fetchBbApiDaily, fetchBbApiLive, fetchCallWindows } from '@core/import/bbApiRepo';
import { liveTotalsOf, type BbApiLive } from '@core/import/bluebeanApiLive';
import { coefficientOf, estimateHours, styleCoefficient, windowHoursByCode, windowsOfDoc, type CallWindow } from '@core/import/workEstimate';
import MiniChart from '@core/dashboard/MiniChart';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { dayColorOf, dayTextClass, holidayName } from '@core/lib/holidays';
import { HOUR_BANDS, type Company, type DailySummary, type DateStr, type MonthStr, type MonthTargets, type Operator } from '@core/lib/types';
import { resolveCompanyByCode } from '@core/import/activity';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchOperators } from '@core/masters/operatorRepo';
import { formatCount, formatCountDiff, formatDecimal, formatDuration, formatHours, formatPercent } from '@core/utils/formatters';
import { businessOfBbId, TARGET_PER_HOUR_BY_BUSINESS } from './shiftRepo';
import { planTargets } from '@core/training/training';
import { fetchSubmission, type ShiftSubmission } from './shiftSubmitRepo';
import ShiftNoticePanel from './ShiftNoticePanel';
import { companyBreakdown, dailyAvgPerHour, dailyReport, fiscalMonths, fiscalReport, fiscalYearOf, shiftDaysOf, totalOfLines, weeklyReport, type CompanyLine, type CompareMetric, type DailyLine, type DailyTotal } from './staffReport';


const monthLabelOf = (m: MonthStr) => `${m.slice(0, 4)}年${Number(m.slice(5, 7))}月`;
const shortMonth = (m: MonthStr) => `${Number(m.slice(5, 7))}月`;
const addMonths = (m: MonthStr, n: number): MonthStr => {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, mm - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
/** 件数(目標は不用品だと小数になることがある) */
const fmtCount1 = (v: number | null) => (v === null ? '—' : Number.isInteger(v) ? formatCount(v) : formatDecimal(v, { digits: 1 }));
const fmtH = (v: number | null) => (v === null ? '—' : formatDecimal(v, { digits: 1 }));
/** 達成率は小数点を切り捨てて出す(2026-09-20 発注者指示。D-98) */
const floorPercent = (v: number | null) => (v === null ? '—' : `${Math.floor(v).toLocaleString('ja-JP')}%`);

/** 土曜・日曜祝日の列の印(設計書 修正3: 土曜=surface-hover / 日曜=網掛け) */
function dayClass(date: DateStr, today: DateStr): string {
  const c = dayColorOf(date);
  return [c === 'red' ? 'is-sunday' : c === 'blue' ? 'is-holiday' : '', date === today ? 'is-current-date' : ''].filter(Boolean).join(' ');
}

export default function StaffMyPage() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const today = todayStr();
  /** 日別(その日の結果・速報)・週別(その週の毎日)・月別(成績表)を切り替える。既定は日別 */
  const view: 'day' | 'week' | 'month' = params.get('view') === 'month' ? 'month' : params.get('view') === 'week' ? 'week' : 'day';
  const dateParam = params.get('date');
  const date: DateStr = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? (dateParam as DateStr) : today;
  const monthParam = params.get('month');
  const month: MonthStr = view !== 'month' ? (date.slice(0, 7) as MonthStr) : monthParam && /^\d{4}-\d{2}$/.test(monthParam) ? monthParam : today.slice(0, 7);
  const isToday = date === today;
  const fy = fiscalYearOf(month);
  const fyMonths = useMemo(() => fiscalMonths(fy), [fy]);
  const fyFrom = `${fy}-04-01` as DateStr;
  const fyEnd = `${fy + 1}-03-31` as DateStr;
  const fyTo = (fyEnd < today ? fyEnd : today) as DateStr;

  /** 見る人。admin は ?op= で切り替えられる。operator は自分に固定 */
  const opId = isAdmin ? (params.get('op') ?? '') : (user?.op_id ?? '');
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [targets, setTargets] = useState<MonthTargets | null>(null);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [submission, setSubmission] = useState<ShiftSubmission | null>(null);
  /** BlueBean API の記録。[速報(bb_api_live), 1日ぶん(bb_api_daily)]。どちらを使うかは内部IDが入っているほうで決める */
  const [apiDocs, setApiDocs] = useState<{ live: BbApiLive | null; daily: BbApiLive | null }>({ live: null, daily: null });
  const [companies, setCompanies] = useState<Company[]>([]);
  /** 係数を出すための過去の窓(日付 → 内部ID → 業務コード → 最初と最後の架電) */
  const [pastWindows, setPastWindows] = useState<Map<DateStr, Map<string, Record<string, CallWindow>>>>(new Map());

  // 年度の日次集計(全員。全体平均に使う)は年度が変わったときだけ読み直す
  useEffect(() => {
    let alive = true;
    setLoading(true);
    void Promise.all([fetchSummariesRange(fyFrom, fyTo), fetchMonthTargets(`${fy}-04`), isAdmin ? fetchOperators() : Promise.resolve([] as Operator[]), fetchCompanies()])
      .then(([s, t, ops, cs]) => {
        if (!alive) return;
        setSummaries(s);
        setTargets(t);
        setOperators(ops);
        setCompanies(cs);
      })
      .catch((e: unknown) => {
        if (alive) toast(e instanceof Error ? e.message : '読み込みに失敗しました');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [fy, fyFrom, fyTo, isAdmin, toast]);

  // その月のシフト提出(提出・修正シフト時間)
  useEffect(() => {
    let alive = true;
    if (!opId) {
      setSubmission(null);
      return;
    }
    void fetchSubmission(month, opId)
      .then((s) => alive && setSubmission(s))
      .catch(() => alive && setSubmission(null));
    return () => {
      alive = false;
    };
  }, [month, opId]);

  /**
   * 選んだ日の BlueBean API の結果(bb_api_live/{日付})。今日なら速報(サーバーが30分おきに書く)で、
   * 開いている間も3分ごとに読み直す。過去の日はその日に取り込んだ記録(時間帯別の通話回数に使う)
   */
  useEffect(() => {
    let alive = true;
    const load = () =>
      void Promise.all([fetchBbApiLive(date), fetchBbApiDaily(date) as Promise<BbApiLive | null>])
        .then(([l, d]) => alive && setApiDocs({ live: l, daily: d }))
        .catch(() => alive && setApiDocs({ live: null, daily: null }));
    load();
    if (!isToday) return () => void (alive = false);
    const timer = setInterval(load, 180_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [date, isToday]);

  // 推定に使う係数の材料(選んだ日より前の45日分の窓)
  useEffect(() => {
    let alive = true;
    const from = new Date(`${date}T00:00:00Z`);
    from.setUTCDate(from.getUTCDate() - 45);
    void fetchCallWindows(from.toISOString().slice(0, 10) as DateStr, date)
      .then((w) => alive && setPastWindows(w))
      .catch(() => alive && setPastWindows(new Map()));
    return () => {
      alive = false;
    };
  }, [date]);

  /** 年度の全員の行(日次【集計】と同じ計算。売上は使わない) */
  const allRows = useMemo(() => (targets ? summaries.flatMap((s) => s.staff.map((st) => staffMetrics(st, s, targets))) : ([] as StaffMetrics[])), [summaries, targets]);
  const myRows = useMemo(() => allRows.filter((r) => r.op_id === opId), [allRows, opId]);
  const shifts = useMemo(() => shiftDaysOf(submission, (bbId) => businessOfBbId(operators, bbId)), [submission, operators]);
  /**
   * 取得件数が確定している日 = **対応履歴(顧客情報レポート)を取り込んだ日**。
   * OPレポートだけ取り込んだ日は稼働・通話はあるが取得は0のままなので、0件ではなく「—(未確定)」として扱う
   */
  const importedDates = useMemo(() => new Set(summaries.filter((s) => (s.sources?.activity?.length ?? 0) > 0).map((s) => s.date)), [summaries]);
  const daily = useMemo(() => dailyReport(month, myRows, shifts, importedDates), [month, myRows, shifts, importedDates]);
  const fiscal = useMemo(() => fiscalReport(fy, opId, allRows), [fy, opId, allRows]);
  /** 週ごとの結果(月別の週の推移グラフ・週別) */
  const weeks = useMemo(() => weeklyReport(month, daily.lines, allRows), [month, daily.lines, allRows]);
  const avgByDate = useMemo(() => dailyAvgPerHour(allRows.filter((r) => r.date.startsWith(month))), [allRows, month]);
  const week = weeks.find((w) => w.dates.includes(date)) ?? weeks[0];

  const me = operators.find((o) => o.id === opId);
  const [opQuery, setOpQuery] = useState('');
  // 選んでいる人が変わったら、検索欄にその人の名前を出す
  useEffect(() => {
    if (me) setOpQuery(`${me.name}(${me.op_code})`);
  }, [me]);
  const name = me?.name ?? myRows[0]?.name ?? '';
  const code = me?.op_code ?? myRows[0]?.op_code ?? '';
  const monthLabel = monthLabelOf(month);
  const t = daily.total;
  const remain = t.appointments - t.target;

  // 名前で検索して選ぶ(2026-10-15 発注者指示)。候補は在籍中の人。「名前(管理番号)」に一致したら切り替える
  const activeOps = operators.filter((o) => o.status === 'active');
  const opLabel = (o: Operator) => `${o.name}(${o.op_code})`;
  const pickByText = (text: string, loose: boolean) => {
    const exact = activeOps.find((o) => opLabel(o) === text);
    if (exact) return setParam('op', exact.id);
    if (!loose) return;
    const key = text.replace(/\s/g, '');
    const hits = key ? activeOps.filter((o) => o.name.replace(/\s/g, '').includes(key) || o.op_code.includes(key)) : [];
    if (hits.length === 1) {
      setOpQuery(opLabel(hits[0]!));
      setParam('op', hits[0]!.id);
    }
  };
  const picker = isAdmin && (
    <label className="ui-field-inline">
      <span className="ui-seg-label">アポインター</span>
      <input
        className="ui-input staff-op-search"
        list="staff-op-list"
        placeholder="名前で検索"
        aria-label="アポインターを名前で検索"
        value={opQuery}
        onChange={(e) => {
          setOpQuery(e.target.value);
          pickByText(e.target.value, false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') pickByText((e.target as HTMLInputElement).value, true);
        }}
        onBlur={(e) => pickByText(e.target.value, true)}
      />
      <datalist id="staff-op-list">
        {activeOps.map((o) => (
          <option key={o.id} value={opLabel(o)} />
        ))}
      </datalist>
    </label>
  );

  const dayLabel = `${Number(date.slice(5, 7))}月${Number(date.slice(8, 10))}日(${weekdayLabel(date).replace(/[()]/g, '')})`;
  const md = (d: DateStr) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
  const weekLabel = week ? `${Number(month.slice(5, 7))}月 W${week.no}(${md(week.from)}〜${md(week.to)})` : '';
  /** 土=青・日祝=赤(システム全体) */
  const dayColorClass = dayTextClass(date);
  const header = (
    <>
      <PageHeader title="本人成績" meta={[name && `${name}(${code})`, view === 'day' ? dayLabel : view === 'week' ? weekLabel : monthLabel].filter(Boolean).join(' ・ ')} />
      <FilterBar label={view === 'day' ? '表示する人と日' : view === 'week' ? '表示する人と週' : '表示する人と月'}>
        {picker}
        <SegmentedControl
          label="表示"
          value={view}
          onChange={(v) => {
            const next = new URLSearchParams(params);
            next.set('view', v);
            if (v === 'month') next.set('month', month);
            else next.set('date', date);
            setParams(next, { replace: true });
          }}
          options={[
            { key: 'day', label: '日別' },
            { key: 'week', label: '週別' },
            { key: 'month', label: '月別' },
          ]}
        />
        {view === 'week' && week ? (
          <>
            <span className="ui-date-nav">
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setParam('date', addDays(week.from, -1))} aria-label="前の週">
                <IconChevronLeft size={16} />
              </button>
              <input type="date" className="ui-input" aria-label="週の中の日" value={date} onChange={(e) => e.target.value && setParam('date', e.target.value)} />
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setParam('date', addDays(week.to, 1))} aria-label="次の週" disabled={week.to >= today}>
                <IconChevronRight size={16} />
              </button>
            </span>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setParam('date', today)} disabled={!!week.dates.includes(today)}>
              今週
            </button>
            <span className="ui-period-label">{weekLabel}</span>
            <span className="ui-hint">週は月曜始まり・月の中で数えます</span>
          </>
        ) : view === 'day' ? (
          <>
            <span className="ui-date-nav">
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setParam('date', addDays(date, -1))} aria-label="前の日">
                <IconChevronLeft size={16} />
              </button>
              <input type="date" className="ui-input" aria-label="日付" value={date} onChange={(e) => e.target.value && setParam('date', e.target.value)} />
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setParam('date', addDays(date, 1))} aria-label="次の日" disabled={date >= today}>
                <IconChevronRight size={16} />
              </button>
            </span>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setParam('date', today)} disabled={isToday}>
              今日
            </button>
            <span className={`ui-period-label ${dayColorClass}`}>{dayLabel}</span>
          </>
        ) : (
          <>
            <span className="ui-date-nav">
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setParam('month', addMonths(month, -1))} aria-label="前の月">
                <IconChevronLeft size={16} />
              </button>
              <input type="month" className="ui-input" aria-label="月" value={month} onChange={(e) => e.target.value && setParam('month', e.target.value)} />
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setParam('month', addMonths(month, 1))} aria-label="次の月">
                <IconChevronRight size={16} />
              </button>
            </span>
            <span className="ui-period-label">{monthLabel}</span>
            <span className="ui-hint">{fy}年度(4月〜3月)</span>
          </>
        )}
      </FilterBar>
    </>
  );

  if (loading) {
    return (
      <div className="screen full">
        {header}
        <div className="ui-panel">
          <div className="ui-empty">読み込み中…</div>
        </div>
      </div>
    );
  }
  if (!opId) {
    return (
      <div className="screen full">
        {header}
        <div className="ui-panel">
          <div className="ui-empty">{isAdmin ? 'アポインターを選んでください(本人がログインしたときに見える画面です)' : 'アカウントにアポインターが紐づいていません。管理者に連絡してください。'}</div>
        </div>
      </div>
    );
  }

  const hasShift = t.corrected > 0;
  const lines = daily.lines;
  /** その月に本人の日次集計が1日でもあるか(無ければ 0 ではなく「—」) */
  const hasData = lines.some((l) => l.hours !== null);
  const orDash = (text: string) => (hasData ? text : '—');

  // ---- 日割りの行(シートの並び) ----
  type Row = { key: string; label: React.ReactNode; total: React.ReactNode; cell: (l: DailyLine) => React.ReactNode; cls?: string; isKey?: boolean; tag?: string };
  const toneOfDiff = (v: number | null): ValueTone => (v !== null && v < 0 ? 'warn' : '');
  const toneOfAch = (v: number | null): ValueTone => (v === null ? '' : v >= 100 ? 'good' : 'warn');
  /**
   * 通話時間Av・後処理時間Av・1時間あたりの通話回数の目標。トレーニング経過(面談予定日の目標)とそろえる(2026-10-20 発注者回答「揃える」)。
   * 給湯器だけ(不用品は目標なし)。給湯器のIDを持つ人は給湯器の目標で見る
   */
  const callTarget = planTargets(!me || me.bb_accounts.some((a) => a.business === 'kyutoki') ? 'kyutoki' : 'fuyohin');
  const offTalk = (v: number | null) => !!callTarget && v !== null && Math.round(v * 10) / 10 > callTarget.talkAvgSec;
  const offAfter = (v: number | null) => !!callTarget && v !== null && Math.round(v * 10) / 10 > callTarget.afterAvgSec;
  const offCph = (v: number | null) => !!callTarget && v !== null && Math.round(v * 10) / 10 < callTarget.callsPerHour;
  const toneOfTalk = (v: number | null): ValueTone => (offTalk(v) ? 'warn' : '');
  const toneOfAfter = (v: number | null): ValueTone => (offAfter(v) ? 'warn' : '');
  const toneOfCph = (v: number | null): ValueTone => (offCph(v) ? 'warn' : '');
  /**
   * 通話時間Av・後処理時間Av・1時間あたりの通話回数のカード(2026-10-20 発注者指示)。週別・月別は日割りの合計欄と同じ「日ごとの値の平均」。
   * 目標(給湯器: 通話22秒以下・後処理6秒以下・80回/h以上。トレーニング経過と同じ)から外れたら注意の色
   */
  const callCards = (v: { talkAvg: number | null; afterAvg: number | null; callsPerHour: number | null }, foot: string, extra?: React.ReactNode) => (
    <div className="kpi-grid">
      <MetricCard
        label="通話時間Av"
        value={formatDuration(v.talkAvg)}
        valueTone={offTalk(v.talkAvg) ? 'warning' : undefined}
        status={offTalk(v.talkAvg) ? <StatusBadge kind="warning" label="目標外" /> : undefined}
        target={callTarget ? `目標 ${formatDuration(callTarget.talkAvgSec)} 以下` : undefined}
        footnote={foot}
      />
      <MetricCard
        label="後処理時間Av"
        value={formatDuration(v.afterAvg)}
        valueTone={offAfter(v.afterAvg) ? 'warning' : undefined}
        status={offAfter(v.afterAvg) ? <StatusBadge kind="warning" label="目標外" /> : undefined}
        target={callTarget ? `目標 ${formatDuration(callTarget.afterAvgSec)} 以下` : undefined}
        footnote={foot}
      />
      <MetricCard
        label="1時間あたりの通話回数"
        value={fmtH(v.callsPerHour)}
        unit={v.callsPerHour === null ? undefined : '回'}
        valueTone={offCph(v.callsPerHour) ? 'warning' : undefined}
        status={offCph(v.callsPerHour) ? <StatusBadge kind="warning" label="目標外" /> : undefined}
        target={callTarget ? `目標 ${formatCount(callTarget.callsPerHour)}回 以上` : undefined}
        footnote={foot}
      />
      {extra}
    </div>
  );
  const rowsFor = (t: DailyTotal, orDash: (text: string) => string): Row[] => [
    { key: 'submitted', label: '提出シフト時間', total: fmtH(t.submitted), cell: (l) => fmtH(l.submitted) },
    { key: 'corrected', label: '修正シフト時間', total: fmtH(t.corrected), cell: (l) => (l.absent ? <span className="cell-na">{l.absent === 'same_day' ? '当欠' : '欠勤'}</span> : fmtH(l.corrected)) },
    {
      key: 'target',
      label: (
        <HelpTip help={{ title: '目標', what: '修正シフト時間 × 2.0件(不用品の日は3時間で1件。シフト表と同じ)', better: '—' }}>
          目標(修正シフト時間 × 2.0件)
        </HelpTip>
      ),
      total: fmtCount1(t.target),
      cell: (l) => fmtCount1(l.target),
      cls: 'is-emphasis',
      isKey: true,
      tag: '目標',
    },
    { key: 'hours', label: '実稼働時間', total: orDash(fmtH(t.hours)), cell: (l) => fmtH(l.hours), cls: 'group-start' },
    { key: 'appointments', label: '実取得件数', total: orDash(formatCount(t.appointments)), cell: (l) => formatCount(l.appointments), cls: 'is-emphasis', isKey: true, tag: '結果' },
    {
      key: 'diff',
      label: (
        <HelpTip help={{ title: '目標対比', what: '取得件数 − 目標(シフトが入っている日だけ)。シフトがあって稼働が無い日は取得0として数える', better: 'プラスが良い' }}>
          目標対比
        </HelpTip>
      ),
      total: <ToneValue tone={toneOfDiff(t.diff)}>{formatCountDiff(t.diff)}</ToneValue>,
      cell: (l) => <ToneValue tone={toneOfDiff(l.diff)}>{l.diff === null ? '' : formatCountDiff(Math.round(l.diff * 10) / 10)}</ToneValue>,
    },
    {
      key: 'achievement',
      label: '目標達成率',
      // 合計は月の目標(まだ来ていない日のシフトも含む)に対する今の率なので、色は付けない
      total: formatPercent(t.achievement),
      cell: (l) => (l.achievement === null ? '' : <ToneValue tone={toneOfAch(l.achievement)}>{formatPercent(l.achievement)}</ToneValue>),
    },
    {
      key: 'perHour',
      label: (
        <HelpTip help={{ title: '1時間あたり取得件数', what: '取得件数 ÷ 実稼働時間(日次【集計】の「見込/h」は稼働合計で割るため、値が違います)', better: '高いほど良い' }}>
          1時間あたり取得件数
        </HelpTip>
      ),
      total: fmtH(t.perHour),
      cell: (l) => fmtH(l.perHour),
      isKey: true,
    },
    { key: 'calls', label: '通話回数', total: orDash(formatCount(t.calls)), cell: (l) => formatCount(l.calls), cls: 'group-start' },
    {
      key: 'talkAvg',
      label: callTarget ? `通話時間Av(目標 ${formatDuration(callTarget.talkAvgSec)})` : '通話時間Av',
      total: <ToneValue tone={toneOfTalk(t.talkAvg)}>{formatDuration(t.talkAvg)}</ToneValue>,
      cell: (l) => <ToneValue tone={toneOfTalk(l.talkAvg)}>{formatDuration(l.talkAvg)}</ToneValue>,
    },
    {
      key: 'afterAvg',
      label: callTarget ? `後処理時間Av(目標 ${formatDuration(callTarget.afterAvgSec)})` : '後処理時間Av',
      isKey: true,
      total: <ToneValue tone={toneOfAfter(t.afterAvg)}>{formatDuration(t.afterAvg)}</ToneValue>,
      cell: (l) => <ToneValue tone={toneOfAfter(l.afterAvg)}>{formatDuration(l.afterAvg)}</ToneValue>,
    },
    {
      key: 'cph',
      label: callTarget ? `1時間あたりの通話回数(目標 ${callTarget.callsPerHour}回)` : '1時間あたりの通話回数',
      isKey: true,
      total: <ToneValue tone={toneOfCph(t.callsPerHour)}>{fmtH(t.callsPerHour)}</ToneValue>,
      cell: (l) => <ToneValue tone={toneOfCph(l.callsPerHour)}>{fmtH(l.callsPerHour)}</ToneValue>,
    },
  ];
  const dailyRows = rowsFor(t, orDash);

  /** 日割りの表(月別の「日割り結果」と週別で使う) */
  const dailyTable = (ls: DailyLine[], rows: Row[], caption: string) => (
    <div className="report-scroll is-matrix">
      <table className="report-table is-wide is-compact no-stack staff-daily">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th scope="col" rowSpan={2} className="is-sticky is-sticky-edge staff-daily-label" style={{ left: 0 }}>
              項目
            </th>
            {ls.map((l) => (
              <th key={l.date} scope="col" className={`is-num staff-daily-day ${dayClass(l.date, today)} ${dayTextClass(l.date)}`} title={holidayName(l.date) ?? undefined}>
                {Number(l.date.slice(8, 10))}日
              </th>
            ))}
            <th scope="col" rowSpan={2} className="is-num col-total is-sticky-right">
              合計
            </th>
          </tr>
          <tr>
            {ls.map((l) => (
              <th key={l.date} scope="col" className={`is-num staff-daily-day ${dayClass(l.date, today)} ${dayTextClass(l.date)}`}>
                {weekdayLabel(l.date).replace(/[()]/g, '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={r.cls}>
              <th scope="row" className={`is-sticky is-sticky-edge staff-daily-label${r.isKey ? ' is-key' : ''}`} style={{ left: 0 }}>
                {r.tag && <span className="key-tag">{r.tag}</span>}
                {r.label}
              </th>
              {ls.map((l) => (
                <td key={l.date} className={`is-num ${dayClass(l.date, today)}`}>
                  {r.cell(l)}
                </td>
              ))}
              <td className="is-num col-total is-sticky-right">{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /** 毎日の推移グラフ2つ(取得件数と目標/1時間取得件数の本人と全体平均)。週別・月別で使う */
  const dailyCharts = (ls: DailyLine[], meta: string) => {
    const labels = ls.map((l) => `${Number(l.date.slice(8, 10))}(${weekdayLabel(l.date).replace(/[()]/g, '')})`);
    return (
      <div className="analysis-grid">
        <Panel title="毎日の取得件数と目標" meta={meta}>
          <MiniChart
            ariaLabel="毎日の取得件数と目標"
            labels={labels}
            series={[
              { key: 'appt', label: '取得件数', values: ls.map((l) => l.appointments) },
              { key: 'target', label: '目標', kind: 'target', values: ls.map((l) => l.target) },
            ]}
            format={fmtRate1}
            labelEvery={ls.length > 10 ? undefined : 1}
            height={180}
          />
        </Panel>
        <Panel title="毎日の1時間取得件数" meta="本人と全体平均">
          <MiniChart
            ariaLabel="毎日の1時間取得件数(本人と全体平均)"
            labels={labels}
            series={[
              { key: 'mine', label: '自数', values: ls.map((l) => l.perHour) },
              { key: 'avg', label: '全体平均', values: ls.map((l) => avgByDate.get(l.date) ?? null) },
            ]}
            format={fmtRate1}
            labelEvery={ls.length > 10 ? undefined : 1}
            height={180}
          />
        </Panel>
      </div>
    );
  };

  /** 会社別の 稼働時間・取得件数・1時間あたり(横棒。2026-10-15 発注者指示) */
  const companyChart = (list: CompanyLine[], meta: string) => {
    const maxH = Math.max(0, ...list.map((c) => c.hours));
    const maxA = Math.max(0, ...list.map((c) => c.appointments));
    const nameOf = (id: string) => (id ? (companies.find((c) => c.id === id)?.name ?? id) : '割振なし');
    return (
      <Panel title="会社別の稼働時間と取得件数" meta={meta} note="取得件数は、対応履歴を取り込んだ日だけ数えます">
        {list.length === 0 ? (
          <div className="ui-empty">この期間の稼働はありません</div>
        ) : (
          <div className="staff-co" role="table" aria-label="会社別の稼働時間と取得件数">
            <div className="staff-co-row staff-co-head" role="row">
              <span role="columnheader">会社</span>
              <span role="columnheader">稼働時間</span>
              <span role="columnheader">取得件数</span>
              <span role="columnheader" className="is-num">件/h</span>
            </div>
            {list.map((c) => (
              <div key={c.companyId || 'none'} className="staff-co-row" role="row">
                <span role="cell" className="staff-co-name">
                  {nameOf(c.companyId)}
                </span>
                <span role="cell" className="staff-co-cell">
                  <span className="staff-co-bar">
                    <span className="staff-co-fill is-hours" style={{ width: `${maxH > 0 ? (c.hours / maxH) * 100 : 0}%` }} />
                  </span>
                  <span className="staff-co-val">{formatDecimal(c.hours, { digits: 1, unit: 'h' })}</span>
                </span>
                <span role="cell" className="staff-co-cell">
                  <span className="staff-co-bar">
                    <span className="staff-co-fill is-appt" style={{ width: `${maxA > 0 ? (c.appointments / maxA) * 100 : 0}%` }} />
                  </span>
                  <span className="staff-co-val">{formatCount(c.appointments)}件</span>
                </span>
                <span role="cell" className="staff-co-val is-num">
                  {fmtH(c.perHour)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    );
  };

  // ---- 週別(その週の毎日) ----
  const weekLines = week ? lines.filter((l) => week.dates.includes(l.date)) : [];
  const wt = totalOfLines(weekLines);
  const weekHasData = weekLines.some((l) => l.hours !== null);
  const weekOrDash = (text: string) => (weekHasData ? text : '—');
  const weekHasShift = wt.corrected > 0;
  const weekRemain = wt.appointments - wt.target;
  const fmtRate1 = (v: number) => formatDecimal(v, { digits: 1 });

  // ---- 全体平均との比較 ----
  const COMPARE: { key: CompareMetric; label: React.ReactNode; fmt: (v: number | null) => string; higherIsBetter: boolean }[] = [
    { key: 'perHour', label: '1時間取得件数', fmt: fmtH, higherIsBetter: true },
    {
      key: 'talkPerAppt',
      label: (
        <HelpTip help={{ title: '1取得あたりの通話時間', what: '日ごとの「通話時間 ÷ 取得件数」の平均。現行シートで「1通話あたりの通話時間」と書かれている行(式は 1取得あたり)', better: '短いほど良い' }}>
          1取得あたりの通話時間
        </HelpTip>
      ),
      fmt: formatDuration,
      higherIsBetter: false,
    },
    { key: 'afterAvg', label: '後処理時間Av', fmt: formatDuration, higherIsBetter: false },
    { key: 'callsPerHour', label: '1時間あたりの通話回数', fmt: fmtH, higherIsBetter: true },
  ];
  /** 全体平均と比べて 良い=緑の太字 / 悪い=赤(現行シートと同じ強調) */
  const toneVsAvg = (mine: number | null, avg: number | null, higher: boolean): ValueTone => {
    if (mine === null || avg === null || mine === avg) return '';
    return (higher ? mine > avg : mine < avg) ? 'good' : 'negative';
  };
  const monthCls = (m: MonthStr) => (m === month ? 'is-current-date' : '');

  // ---- 日別(選んだ日の結果。今日は速報) ----
  const internalIds = (me?.bb_accounts ?? []).map((a) => a.bb_internal_id ?? '').filter((id) => id !== '');
  /**
   * 使う API の記録。今日は速報が正。過去の日は **この人の記録が入っているほう** を選ぶ
   * (速報が途中までの日や、1日ぶんだけ取り直した日があるため)
   */
  const live = (() => {
    const { live: l, daily: d } = apiDocs;
    if (isToday) return l ?? d;
    const mine = (x: BbApiLive | null) => !!x && internalIds.some((id) => x.operators?.[id] !== undefined);
    return mine(l) ? l : mine(d) ? d : (l ?? d);
  })();
  const liveTotals = liveTotalsOf(live, internalIds);
  /** その日の窓(会社ごとの 最初〜最後の架電)の合計。内部IDが複数ある人はまとめる */
  const windowHoursOn = (day: Map<string, Record<string, CallWindow>> | undefined) =>
    day ? Math.round(internalIds.reduce((a, id) => a + windowHoursByCode(day.get(id) ?? {}).total, 0) * 100) / 100 : 0;
  /** 確定した実稼働(日ごと)。係数を出す材料 */
  const workedByDate = new Map<DateStr, number>();
  for (const r of myRows) workedByDate.set(r.date, (workedByDate.get(r.date) ?? 0) + r.worked_hours);
  const account = me?.bb_accounts?.[0];
  const coef = coefficientOf(
    [...pastWindows].filter(([d]) => d < date).map(([d, day]) => ({ date: d, worked: workedByDate.get(d) ?? 0, window: windowHoursOn(day) })),
    account ? styleCoefficient(account.work_style, account.pay_type) : 0.9,
  );

  const dayLine = lines.find((l) => l.date === date) ?? null;
  const dayRows = myRows.filter((r) => r.date === date);
  const dayAgg = dayRows.length > 0 ? sumAgg(dayRows) : null;
  const dayD = dayAgg ? derived(dayAgg) : null;
  /** その日の取得件数が確定しているか(対応履歴を取り込んだか) */
  const apptConfirmed = importedDates.has(date);
  /** 確定(日次集計)があればそれ、無ければ速報の数字 */
  const useLive = (dayAgg === null || !apptConfirmed) && liveTotals !== null;
  const dayAppointments = dayAgg && apptConfirmed ? dayAgg.appointments : (liveTotals?.appointments ?? null);
  const dayCalls = dayAgg ? dayAgg.calls : (liveTotals?.calls ?? null);
  const dayTarget = dayLine?.target ?? null;
  const dayDiff = dayAppointments !== null && dayTarget !== null ? dayAppointments - dayTarget : null;
  const dayAchievement = dayAppointments !== null && dayTarget !== null && dayTarget > 0 ? (dayAppointments / dayTarget) * 100 : null;
  /** 実稼働: 確定が無い日は 窓 × 係数 の推定 */
  const windowH = windowHoursOn(windowsOfDoc(live));
  const estHours = windowH > 0 ? estimateHours(windowH, coef.value) : null;
  const confirmedHours = dayAgg && dayAgg.hours > 0 ? dayAgg.hours : null;
  const dayHours = confirmedHours ?? estHours;
  const hoursIsEstimate = confirmedHours === null && estHours !== null;
  const dayPerHour = dayHours !== null && dayHours > 0 && dayAppointments !== null ? dayAppointments / dayHours : null;
  /** 架電の時間帯(いちばん早い開始 〜 いちばん遅い終了) */
  const daySpan = (() => {
    const all = internalIds.flatMap((id) => Object.values(windowsOfDoc(live).get(id) ?? {}));
    if (all.length === 0) return null;
    const first = all.map((w) => w.first).sort()[0];
    const last = all.map((w) => w.last).sort().pop();
    return first && last ? `${first.slice(0, 5)}〜${last.slice(0, 5)}` : null;
  })();
  /** 時間帯別の目標: その時間帯にシフトが掛かっている時間 × 目標件数/h(不用品は3時間で1件) */
  const hourTargets = (() => {
    const day = submission?.days?.[date.slice(8, 10)];
    if (!day || day.status === 'absent' || day.status === 'same_day') return null;
    const start = day.start ?? null;
    const end = day.end ?? null;
    if (start === null || end === null || end <= start) return null;
    const rate = TARGET_PER_HOUR_BY_BUSINESS[businessOfBbId(operators, submission?.bb_id ?? '')];
    return HOUR_BANDS.map((h) => {
      const cover = Math.max(0, Math.min(end, h + 1) - Math.max(start, h));
      return Math.round(cover * rate * 10) / 10;
    });
  })();

  /** 時間帯別(9〜19時台)。取得は確定を優先、架電は API の記録があるときだけ */
  const hourAppt = dayAgg && apptConfirmed ? dayAgg.by_hour : (liveTotals?.by_hour ?? null);
  const hourCalls = (liveTotals?.calls_by_hour ?? []).some((v) => v > 0) ? (liveTotals?.calls_by_hour ?? null) : null;
  const daySum = (list: number[]) => list.reduce((a, b) => a + b, 0);
  /** 会社ごとの時間帯別(2社以上に架電した日だけ出す)。確定は日次集計から、今日は速報から */
  const hourByCompany: { name: string; appt: number[]; total: number; calls: number[] | null; callsTotal: number | null }[] = (() => {
    type Row = { name: string; appt: number[]; total: number; calls: number[] | null; callsTotal: number | null };
    const out = new Map<string, Row>();
    const empty = (name: string, withCalls: boolean): Row => ({ name, appt: HOUR_BANDS.map(() => 0), total: 0, calls: withCalls ? HOUR_BANDS.map(() => 0) : null, callsTotal: withCalls ? 0 : null });
    if (dayAgg && apptConfirmed) {
      for (const r of dayRows) {
        const name = r.company_id ? (companies.find((c) => c.id === r.company_id)?.name ?? r.company_id) : '割振なし';
        const cur = out.get(name) ?? empty(name, false);
        cur.appt = cur.appt.map((v, i) => v + (r.by_hour[i] ?? 0));
        cur.total += r.appointments;
        out.set(name, cur);
      }
    } else if (liveTotals) {
      for (const id of internalIds) {
        for (const [code, stat] of Object.entries(live?.operators[id]?.by_code ?? {})) {
          const name = resolveCompanyByCode(code, companies)?.name ?? code;
          const cur = out.get(name) ?? empty(name, true);
          cur.appt = cur.appt.map((v, i) => v + (stat.by_hour?.[i] ?? 0));
          cur.total += stat.appointments;
          cur.calls = (cur.calls ?? HOUR_BANDS.map(() => 0)).map((v, i) => v + (stat.calls_by_hour?.[i] ?? 0));
          cur.callsTotal = (cur.callsTotal ?? 0) + stat.calls;
          out.set(name, cur);
        }
      }
    }
    return [...out.values()].filter((x) => dayAgg !== null || x.total > 0 || (x.callsTotal ?? 0) > 0);
  })();
  /** 9〜19時台の外(早朝・夜)の件数。合計とのズレを見せるための列 */
  const outsideAppt = Math.max(0, (dayAppointments ?? 0) - daySum(hourAppt ?? []));
  const outsideCalls = hourCalls && dayCalls !== null ? Math.max(0, dayCalls - daySum(hourCalls)) : null;

  return (
    <div className="screen full">
      {header}
      {/* 管理者がシフトを直したときのお知らせ(本人だけ。D-130) */}
      <ShiftNoticePanel showLink />

      {/* ==== 日別(その日の結果。今日は速報) ==== */}
      {view === 'day' && (
        <>
          <div className="kpi-grid">
            <MetricCard
              emphasis
              label="取得件数"
              value={dayAppointments === null ? '—' : formatCount(dayAppointments)}
              unit={dayAppointments === null ? undefined : '件'}
              status={
                dayTarget === null ? (
                  <StatusBadge kind="noData" label="シフトなし" />
                ) : dayDiff !== null && dayDiff >= 0 ? (
                  <StatusBadge kind="success" label="目標達成" />
                ) : dayDiff !== null ? (
                  <StatusBadge kind="warning" label={`あと ${fmtCount1(-dayDiff)}件`} />
                ) : undefined
              }
              target={dayTarget !== null ? `目標 ${fmtCount1(dayTarget)}件` : undefined}
              diff={dayDiff !== null ? `目標との差 ${formatCountDiff(Math.round(dayDiff * 10) / 10, { unit: '件' })}` : undefined}
              diffTone={dayDiff !== null && dayDiff >= 0 ? 'success' : 'warning'}
              footnote={
                useLive && live
                  ? live.window_to
                    ? `速報(${live.window_to.slice(0, 5)} 時点)`
                    : 'BlueBean API の記録(暫定)'
                  : !apptConfirmed && dayAgg
                    ? '対応履歴(取得件数のCSV)の取込待ち'
                    : undefined
              }
            />
            {/* 「確定」「推定」のバッジを **常に** 出す(2026-09-20 発注者指示。D-103) */}
            <MetricCard
              label="実稼働時間"
              value={fmtH(dayHours)}
              unit={dayHours === null ? undefined : 'h'}
              status={dayHours === null ? undefined : hoursIsEstimate ? <StatusBadge kind="unconfirmed" label="推定" /> : <StatusBadge kind="confirmed" label="確定" />}
              footnote={dayHours === null ? undefined : hoursIsEstimate ? '最初の架電〜最後の架電から推定' : 'オペレーターレポートの実稼働'}
            />
            <MetricCard label="1時間あたりの取得件数" value={fmtH(dayPerHour)} unit={dayPerHour === null ? undefined : '件'} footnote="取得件数 ÷ 実稼働時間" />
          </div>
          {/* 通話時間Av・後処理時間Av・1時間あたりの通話回数(2026-10-20 発注者指示で上のカードの下へ。日次【集計】と同じ数字) */}
          {dayAgg && dayD && callCards({ talkAvg: dayD.talk_avg, afterAvg: dayD.after_avg, callsPerHour: dayD.calls_per_hour }, '日次【集計】と同じ', <MetricCard label="取得率" value={formatPercent(dayD.hit_rate)} footnote="取得 ÷ 架電" />)}

          {dayTarget !== null && dayTarget > 0 && (
            <Panel title="この日の達成率" meta={useLive ? (live?.window_to ? '速報' : 'BlueBean API の記録(暫定)') : undefined}>
              <div className="achievement">
                <div className="achievement-value">
                  <span className={`kpi-value${(dayAchievement ?? 0) >= 100 ? ' tone-success' : ''}`}>
                    {dayAppointments === null ? '—' : formatCount(dayAppointments)}
                    <span className="achievement-of"> ／ {fmtCount1(dayTarget)}件</span>
                  </span>
                  <span className="kpi-unit">達成率 {floorPercent(dayAchievement)}</span>
                </div>
                <ProgressBar value={dayAchievement} label="この日の達成率" goal={(dayAchievement ?? 0) >= 100 ? 'GOAL！' : `目標 ${fmtCount1(dayTarget)}件`} />
              </div>
            </Panel>
          )}

          <Panel
            title="時間帯別"
            meta={[dayLabel, daySpan ? `架電 ${daySpan}` : null, hourByCompany.length > 0 ? `架電会社: ${hourByCompany.map((c) => c.name).join('・')}` : null].filter(Boolean).join(' ・ ')}
            note={hourCalls ? '通話回数・取得率は BlueBean の通話の記録から数えています' : 'この日は BlueBean API の記録が無いため、通話回数・取得率は出せません(「BlueBean API連携」でその日を取得すると入ります)'}
          >
            {hourAppt || hourCalls ? (
              <div className="report-scroll is-matrix">
                <table className="report-table is-wide no-stack staff-hours">
                  <caption className="sr-only">{dayLabel}の時間帯別</caption>
                  <thead>
                    <tr>
                      <th scope="col" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                        項目
                      </th>
                      {HOUR_BANDS.map((h) => (
                        <th key={h} scope="col" className="is-num">
                          {h}時
                        </th>
                      ))}
                      <th scope="col" className="is-num">
                        <HelpTip help={{ title: '時間外', what: '9時〜19時台の外(早朝・夜)の件数', better: '—' }}>時間外</HelpTip>
                      </th>
                      <th scope="col" className="is-num col-total is-sticky-right">
                        合計
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {hourTargets && (
                      <tr>
                        <th scope="row" className="is-sticky is-sticky-edge is-key" style={{ left: 0 }}>
                          <span className="key-tag">目標</span>目標件数
                        </th>
                        {hourTargets.map((v, i) => (
                          <td key={i} className="is-num">
                            {v === 0 ? <span className="cell-na">—</span> : fmtCount1(v)}
                          </td>
                        ))}
                        <td className="is-num">
                          <span className="cell-na">—</span>
                        </td>
                        <td className="is-num col-total is-sticky-right">{fmtCount1(Math.round(daySum(hourTargets) * 10) / 10)}</td>
                      </tr>
                    )}
                    <tr className="is-emphasis">
                      <th scope="row" className="is-sticky is-sticky-edge is-key" style={{ left: 0 }}>
                        <span className="key-tag">結果</span>取得件数
                      </th>
                      {HOUR_BANDS.map((_, i) => (
                        <td key={i} className="is-num">
                          {!hourAppt ? <span className="cell-na">—</span> : hourAppt[i] === 0 ? <span className="cell-na">0</span> : formatCount(hourAppt[i] ?? 0)}
                        </td>
                      ))}
                      <td className="is-num">{!hourAppt ? <span className="cell-na">—</span> : outsideAppt === 0 ? <span className="cell-na">0</span> : formatCount(outsideAppt)}</td>
                      <td className="is-num col-total is-sticky-right">{dayAppointments === null ? <span className="cell-na">—</span> : formatCount(dayAppointments)}</td>
                    </tr>
                    {hourTargets && hourAppt && (
                      <tr>
                        <th scope="row" className="is-sticky is-sticky-edge is-key" style={{ left: 0 }}>
                          目標対比
                        </th>
                        {hourTargets.map((t2, i) => {
                          const d = Math.round(((hourAppt[i] ?? 0) - t2) * 10) / 10;
                          return (
                            <td key={i} className="is-num">
                              {t2 === 0 && (hourAppt[i] ?? 0) === 0 ? <span className="cell-na">—</span> : <ToneValue tone={d >= 0 ? 'good' : 'negative'}>{formatCountDiff(d)}</ToneValue>}
                            </td>
                          );
                        })}
                        <td className="is-num">
                          <span className="cell-na">—</span>
                        </td>
                        <td className="is-num col-total is-sticky-right">
                          <ToneValue tone={(dayAppointments ?? 0) - daySum(hourTargets) >= 0 ? 'good' : 'negative'}>{formatCountDiff(Math.round(((dayAppointments ?? 0) - daySum(hourTargets)) * 10) / 10)}</ToneValue>
                        </td>
                      </tr>
                    )}
                    <tr className="group-start">
                      <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                        通話回数
                      </th>
                      {HOUR_BANDS.map((_, i) => (
                        <td key={i} className="is-num">
                          {hourCalls ? (hourCalls[i] === 0 ? <span className="cell-na">0</span> : formatCount(hourCalls[i] ?? 0)) : <span className="cell-na">—</span>}
                        </td>
                      ))}
                      <td className="is-num">{outsideCalls === null ? <span className="cell-na">—</span> : outsideCalls === 0 ? <span className="cell-na">0</span> : formatCount(outsideCalls)}</td>
                      <td className="is-num col-total is-sticky-right">{hourCalls ? formatCount(dayCalls ?? daySum(hourCalls)) : '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                        取得率
                      </th>
                      {HOUR_BANDS.map((_, i) => (
                        <td key={i} className="is-num">
                          {hourAppt && hourCalls && (hourCalls[i] ?? 0) > 0 ? formatPercent(((hourAppt[i] ?? 0) / (hourCalls[i] ?? 1)) * 100) : <span className="cell-na">—</span>}
                        </td>
                      ))}
                      <td className="is-num">
                        <span className="cell-na">—</span>
                      </td>
                      <td className="is-num col-total is-sticky-right">
                        {dayAppointments !== null && hourCalls && (dayCalls ?? 0) > 0 ? formatPercent((dayAppointments / (dayCalls ?? 1)) * 100) : '—'}
                      </td>
                    </tr>
                    {/* 会社ごとのまとまり(取得件数 → 通話回数 → 取得率)。2社以上に架電した日だけ出す(2026-09-20 発注者指示。D-99) */}
                    {hourByCompany.length > 1 &&
                      hourByCompany.map((c) => (
                        <Fragment key={c.name}>
                          <tr className="group-start">
                            <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                              <span className="hour-company">{c.name}</span>
                              <br />
                              取得件数
                            </th>
                            {c.appt.map((v, i) => (
                              <td key={i} className="is-num">
                                {v === 0 ? <span className="cell-na">0</span> : formatCount(v)}
                              </td>
                            ))}
                            <td className="is-num">{c.total - daySum(c.appt) === 0 ? <span className="cell-na">0</span> : formatCount(c.total - daySum(c.appt))}</td>
                            <td className="is-num col-total is-sticky-right">{formatCount(c.total)}</td>
                          </tr>
                          <tr>
                            <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                              <span className="hour-company">{c.name}</span>
                              <br />
                              通話回数
                            </th>
                            {HOUR_BANDS.map((_, i) => (
                              <td key={i} className="is-num">
                                {c.calls ? ((c.calls[i] ?? 0) === 0 ? <span className="cell-na">0</span> : formatCount(c.calls[i] ?? 0)) : <span className="cell-na">—</span>}
                              </td>
                            ))}
                            <td className="is-num">
                              {c.calls ? ((c.callsTotal ?? 0) - daySum(c.calls) === 0 ? <span className="cell-na">0</span> : formatCount((c.callsTotal ?? 0) - daySum(c.calls))) : <span className="cell-na">—</span>}
                            </td>
                            <td className="is-num col-total is-sticky-right">{c.calls ? formatCount(c.callsTotal ?? 0) : <span className="cell-na">—</span>}</td>
                          </tr>
                          <tr>
                            <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                              <span className="hour-company">{c.name}</span>
                              <br />
                              取得率
                            </th>
                            {HOUR_BANDS.map((_, i) => (
                              <td key={i} className="is-num">
                                {c.calls && (c.calls[i] ?? 0) > 0 ? formatPercent(((c.appt[i] ?? 0) / (c.calls[i] ?? 1)) * 100) : <span className="cell-na">—</span>}
                              </td>
                            ))}
                            <td className="is-num">
                              <span className="cell-na">—</span>
                            </td>
                            <td className="is-num col-total is-sticky-right">{(c.callsTotal ?? 0) > 0 ? formatPercent((c.total / (c.callsTotal ?? 1)) * 100) : <span className="cell-na">—</span>}</td>
                          </tr>
                        </Fragment>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ui-empty">
                {dayAgg
                  ? '時間帯別は、取得件数の取込(対応履歴)か BlueBean API の記録があると出ます'
                  : internalIds.length === 0
                    ? 'この方の BlueBean内部ID が未登録のため、速報を出せません(設定 → BlueBean API連携 で登録)'
                    : 'この日のデータはまだありません'}
              </div>
            )}
          </Panel>

          {/* 会社別(日別にも念のため出す。2026-10-16 発注者指示)。確定した日次集計の数字。今日の速報は取込の翌日から入る */}
          {companyChart(companyBreakdown(myRows, [date], importedDates), dayLabel)}
        </>
      )}

      {/* ==== 週別(その週の毎日。2026-10-15 発注者指示) ==== */}
      {view === 'week' && week && (
        <>
          <div className="kpi-grid">
            <MetricCard emphasis label={`目標(W${week.no})`} value={fmtCount1(weekHasShift ? wt.target : null)} unit={weekHasShift ? '件' : undefined} footnote="修正シフト時間 × 2.0件(不用品は3時間で1件)">
              <MetricRow label="修正シフト時間" value={submission ? formatHours(wt.corrected) : '—'} />
            </MetricCard>
            <MetricCard
              emphasis
              label="取得件数"
              value={weekOrDash(formatCount(wt.appointments))}
              unit={weekHasData ? '件' : undefined}
              status={!weekHasShift ? <StatusBadge kind="noData" label="シフトなし" /> : weekRemain >= 0 ? <StatusBadge kind="success" label="目標達成" /> : <StatusBadge kind="warning" label={`あと ${fmtCount1(-weekRemain)}件`} />}
              target={weekHasShift ? `目標 ${fmtCount1(wt.target)}件` : undefined}
              diff={weekHasShift ? `目標との差 ${formatCountDiff(Math.round(weekRemain * 10) / 10, { unit: '件' })}` : undefined}
              diffTone={weekHasShift ? (weekRemain >= 0 ? 'success' : 'warning') : 'neutral'}
            />
            <MetricCard label="実稼働時間" value={weekOrDash(fmtH(wt.hours))} unit={weekHasData ? 'h' : undefined} />
            <MetricCard label="1時間あたりの取得件数" value={fmtH(wt.perHour)} unit={wt.perHour !== null ? '件' : undefined} footnote="取得件数 ÷ 実稼働時間" />
          </div>
          {callCards(wt, '日ごとの値の平均')}
          {dailyCharts(weekLines, weekLabel)}
          {companyChart(companyBreakdown(myRows, week.dates, importedDates), weekLabel)}
          <Panel title="日割り結果" meta={weekLabel} note="合計欄の 通話時間Av・後処理時間Av・1時間あたりの通話回数 は、日ごとの値の平均です">
            {dailyTable(weekLines, rowsFor(wt, weekOrDash), `日割り結果 ${weekLabel}`)}
          </Panel>
        </>
      )}

      {/* ==== 月別(成績表) ==== */}
      {view === 'month' && (
        <>
      {/* ---- ① その月の結果 ---- */}
      <div className="kpi-grid">
        <MetricCard emphasis label={`目標(${shortMonth(month)})`} value={fmtCount1(hasShift ? t.target : null)} unit={hasShift ? '件' : undefined} footnote="修正シフト時間 × 2.0件(不用品は3時間で1件)">
          <MetricRow label="提出シフト時間" value={submission ? formatHours(t.submitted) : '—'} />
          <MetricRow label="修正シフト時間" value={submission ? formatHours(t.corrected) : '—'} />
        </MetricCard>
        <MetricCard
          emphasis
          label="取得件数"
          value={orDash(formatCount(t.appointments))}
          unit={hasData ? '件' : undefined}
          status={!hasShift ? <StatusBadge kind="noData" label="シフトなし" /> : remain >= 0 ? <StatusBadge kind="success" label="目標達成" /> : <StatusBadge kind="warning" label={`あと ${fmtCount1(-remain)}件`} />}
          target={hasShift ? `目標 ${fmtCount1(t.target)}件` : undefined}
          diff={hasShift ? `目標との差 ${formatCountDiff(Math.round(remain * 10) / 10, { unit: '件' })}` : undefined}
          diffTone={hasShift ? (remain >= 0 ? 'success' : 'warning') : 'neutral'}
        />
        <MetricCard label="実稼働時間" value={orDash(fmtH(t.hours))} unit={hasData ? 'h' : undefined} />
        <MetricCard label="1時間あたりの取得件数" value={fmtH(t.perHour)} unit={t.perHour !== null ? '件' : undefined} footnote="取得件数 ÷ 実稼働時間" />
      </div>
      {callCards(t, '日ごとの値の平均')}

      {/* ---- ② 達成率 ／ 推移 ---- */}
      <div className="analysis-grid">
        <Panel title="取得件数達成率" meta={monthLabel}>
          {hasShift ? (
            <div className="achievement">
              <div className="achievement-value">
                <span className={`kpi-value${(t.achievement ?? 0) >= 100 ? ' tone-success' : ''}`}>
                  {formatCount(t.appointments)}
                  <span className="achievement-of"> ／ {fmtCount1(t.target)}件</span>
                </span>
                <span className="kpi-unit">達成率 {floorPercent(t.achievement)}</span>
              </div>
              <ProgressBar value={t.achievement} label="取得件数達成率" goal={(t.achievement ?? 0) >= 100 ? 'GOAL！' : `目標 ${fmtCount1(t.target)}件`} />
            </div>
          ) : (
            <div className="ui-empty">この月のシフト(修正シフト時間)がありません</div>
          )}
        </Panel>
        <Panel title="1時間取得件数の推移" meta={`${fy}年度 ・ 本人と全体平均`}>
          <MiniChart
            ariaLabel="1時間取得件数の推移(本人と全体平均)"
            labels={fyMonths.map(shortMonth)}
            // シートのグラフと同じく、下の「月別の結果」の 1時間取得件数(自数)と 平均1時間取得件数(全体平均)
            series={[
              { key: 'mine', label: '自数', values: fiscal.months.map((m) => m.perHour) },
              { key: 'avg', label: '全体平均', values: fiscal.months.map((m) => m.avgPerHourDaily) },
            ]}
            format={(v) => formatDecimal(v, { digits: 1 })}
            labelEvery={1}
            height={180}
          />
        </Panel>
      </div>

      {/* ---- 毎日の推移・会社別(2026-10-15 発注者指示「月別も毎日の推移に」) ---- */}
      {dailyCharts(lines, monthLabel)}
      {companyChart(companyBreakdown(myRows, lines.map((l) => l.date), importedDates), monthLabel)}

      {/* ---- ③ 月別の結果(年度) ---- */}
      <Panel title="月別の結果" meta={`${fy}年度(4月〜3月)`}>
        <div className="report-scroll is-matrix">
          <table className="report-table is-wide is-compact no-stack staff-months">
            <caption className="sr-only">月別の結果 {fy}年度</caption>
            <thead>
              <tr>
                <th scope="col" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                  項目
                </th>
                {fyMonths.map((m) => (
                  <th key={m} scope="col" className={`is-num ${monthCls(m)}`}>
                    {shortMonth(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { key: 'hours', label: '実稼働時間(h)', v: (i: number) => (fiscal.months[i]!.hours > 0 ? fmtH(fiscal.months[i]!.hours) : '—') },
                { key: 'hoursTarget', label: '実稼働目標件数', v: (i: number) => (fiscal.months[i]!.hours > 0 ? fmtCount1(fiscal.months[i]!.hoursTarget) : '—') },
                { key: 'appointments', label: '取得件数', v: (i: number) => (fiscal.months[i]!.hours > 0 || fiscal.months[i]!.appointments > 0 ? formatCount(fiscal.months[i]!.appointments) : '—') },
                { key: 'perHour', label: '1時間取得件数', v: (i: number) => fmtH(fiscal.months[i]!.perHour), bold: true },
                {
                  key: 'avg',
                  label: (
                    <HelpTip help={{ title: '平均1時間取得件数', what: 'その月の全員・全日の「1人1日の 取得件数 ÷ 実稼働時間」の平均', better: '本人の 1時間取得件数 と比べる', source: '現行シート「成績表」' }}>
                      平均1時間取得件数(全員)
                    </HelpTip>
                  ),
                  v: (i: number) => fmtH(fiscal.months[i]!.avgPerHourDaily),
                },
              ].map((r: { key: string; label: React.ReactNode; v: (i: number) => string; bold?: boolean }) => (
                <tr key={r.key} className={r.bold ? 'lv-0' : undefined}>
                  <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                    {r.label}
                  </th>
                  {fyMonths.map((m, i) => (
                    <td key={m} className={`is-num ${monthCls(m)}`}>
                      {r.v(i)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* ---- ④ 日割り結果 ---- */}
      <Panel title="日割り結果" meta={monthLabel} note="合計欄の 通話時間Av・後処理時間Av・1時間あたりの通話回数 は、日ごとの値の平均です">
        {dailyTable(lines, dailyRows, `日割り結果 ${monthLabel}`)}
      </Panel>

      {/* ---- ⑤ 全体平均との比較 ---- */}
      <Panel title="全体平均との比較(月別)" meta={`${fy}年度`} note="平均 = その月に稼働した全員の平均。自数が平均より良い月は緑の太字">
        <div className="report-scroll is-matrix">
          <table className="report-table is-wide is-compact no-stack staff-compare">
            <caption className="sr-only">全体平均との比較 {fy}年度</caption>
            <thead>
              <tr>
                <th scope="col" className="is-sticky" style={{ left: 0 }}>
                  項目
                </th>
                <th scope="col" className="is-sticky is-sticky-edge staff-compare-kind">
                  区分
                </th>
                {fyMonths.map((m) => (
                  <th key={m} scope="col" className={`is-num ${monthCls(m)}`}>
                    {shortMonth(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE.flatMap((c, ci) => [
                <tr key={`${c.key}-avg`} className={ci > 0 ? 'group-start' : undefined}>
                  <th scope="rowgroup" rowSpan={2} className="is-sticky" style={{ left: 0 }}>
                    {c.label}
                  </th>
                  <th scope="row" className="is-sticky is-sticky-edge staff-compare-kind">
                    平均
                  </th>
                  {fiscal.compare.map((x) => (
                    <td key={x.month} className={`is-num ${monthCls(x.month)}`}>
                      {c.fmt(x.avg[c.key])}
                    </td>
                  ))}
                </tr>,
                <tr key={`${c.key}-mine`} className="lv-0">
                  <th scope="row" className="is-sticky is-sticky-edge staff-compare-kind">
                    自数
                  </th>
                  {fiscal.compare.map((x) => (
                    <td key={x.month} className={`is-num ${monthCls(x.month)}`}>
                      <ToneValue tone={toneVsAvg(x.mine[c.key], x.avg[c.key], c.higherIsBetter)}>{c.fmt(x.mine[c.key])}</ToneValue>
                    </td>
                  ))}
                </tr>,
              ])}
            </tbody>
          </table>
        </div>
      </Panel>
        </>
      )}
    </div>
  );
}
